'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import type { CaptureField } from '@/lib/capture/types';
import type { PublicCaptureBrand } from '@/lib/capture/publicBrand';
import { PROPOSAL_BRAND } from '@/lib/proposals/brand';

type PublicForm = {
  name: string;
  slug: string;
  mode: 'lead' | 'gate' | 'event';
  fields: CaptureField[];
  thankYouMessage: string;
  calendarUrl?: string | null;
  turnstileEnabled: boolean;
  turnstileSiteKey: string | null;
  honeypotEnabled: boolean;
};

const FALLBACK_BRAND: PublicCaptureBrand = {
  companyName: '',
  tagline: '',
  website: '',
  websiteLabel: '',
  accent: PROPOSAL_BRAND.accent,
  navy900: PROPOSAL_BRAND.navy900,
  navy800: PROPOSAL_BRAND.navy800,
  companyLogoSrc: PROPOSAL_BRAND.logoSrc,
  companyLogoSrcOnDark: PROPOSAL_BRAND.logoSrcOnDark,
  crmLogoSrc: PROPOSAL_BRAND.logoSrc,
  crmLogoSrcOnDark: PROPOSAL_BRAND.logoSrcOnDark,
};

function readUtmParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const sp = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']) {
    const v = sp.get(key);
    if (v) out[key] = v;
  }
  return out;
}

function readProductParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const sp = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  const productSlug = sp.get('productSlug') || sp.get('product_slug');
  const productName = sp.get('productName') || sp.get('product_name');
  if (productSlug) out.productSlug = productSlug;
  if (productName) out.productName = productName;
  return out;
}

function fieldPlaceholder(field: CaptureField): string {
  if (field.type === 'email') return 'you@company.com';
  if (field.type === 'tel') return '+44 …';
  if (field.key === 'company') return 'Company name';
  if (field.key === 'name') return 'Your name';
  if (field.type === 'textarea') return 'How can we help?';
  return field.label;
}

function CaptureBrandLogo({
  brand,
  onDark = true,
}: {
  brand: PublicCaptureBrand;
  onDark?: boolean;
}) {
  const [stage, setStage] = useState<'primary' | 'fallback' | 'hidden'>('primary');
  const primary = onDark ? brand.companyLogoSrcOnDark : brand.companyLogoSrc;
  const fallback = onDark ? brand.companyLogoSrc : brand.companyLogoSrcOnDark;
  const src =
    stage === 'primary'
      ? primary
      : stage === 'fallback'
        ? fallback
        : null;

  if (stage === 'hidden' || !src) {
    if (!brand.companyName) return null;
    return (
      <span className="text-sm font-semibold tracking-tight text-white truncate">
        {brand.companyName}
      </span>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={brand.companyName || 'Company logo'}
      className="h-9 w-auto max-w-[11rem] object-contain object-left"
      onError={() => {
        setStage((current) => {
          if (current === 'primary' && fallback && fallback !== primary) return 'fallback';
          return 'hidden';
        });
      }}
    />
  );
}

function PoweredByFoundry({ crmLogoSrc }: { crmLogoSrc: string }) {
  return (
    <div className="flex items-center justify-center gap-2 pt-3 border-t border-border/60">
      <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Powered by</span>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={crmLogoSrc}
        alt="Foundry CRM"
        className="h-4 w-auto max-w-[7rem] object-contain opacity-80"
      />
    </div>
  );
}

function FormShell({
  brand,
  embed,
  children,
}: {
  brand: PublicCaptureBrand;
  embed: boolean;
  children: React.ReactNode;
}) {
  if (embed) {
    return <div className="p-4 sm:p-5 bg-sheet min-h-full">{children}</div>;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 sm:p-8"
      style={{
        background: `linear-gradient(165deg, ${brand.navy900} 0%, ${brand.navy800} 55%, ${brand.navy900} 100%)`,
      }}
    >
      {children}
    </div>
  );
}

function FormCard({
  brand,
  children,
}: {
  brand: PublicCaptureBrand;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full max-w-[28rem] rounded-card border border-white/10 bg-card shadow-[0_24px_60px_-24px_rgba(0,0,0,0.45)] overflow-hidden">
      <div
        className="px-6 py-5 border-b border-white/10"
        style={{ backgroundColor: brand.navy900 }}
      >
        <CaptureBrandLogo brand={brand} onDark />
      </div>
      <div className="p-6 sm:p-7 space-y-5">{children}</div>
    </div>
  );
}

export default function CaptureFormClient({
  slug,
  embed = false,
}: {
  slug: string;
  embed?: boolean;
}) {
  const [form, setForm] = useState<PublicForm | null>(null);
  const [brand, setBrand] = useState<PublicCaptureBrand>(FALLBACK_BRAND);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{
    message: string;
    unlockUrl?: string | null;
    calendarUrl?: string | null;
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`/api/capture/${encodeURIComponent(slug)}`)
      .then(async (r) => {
        const json = await r.json();
        if (!r.ok) throw new Error(json.message || 'Form unavailable');
        setForm(json.form);
        if (json.brand) setBrand({ ...FALLBACK_BRAND, ...json.brand });
        const initial: Record<string, string> = {};
        for (const f of json.form.fields as CaptureField[]) initial[f.key] = '';
        setValues(initial);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Form unavailable'))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!form?.turnstileEnabled || !form.turnstileSiteKey) return;
    const existing = document.querySelector('script[data-turnstile]');
    if (existing) return;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.dataset.turnstile = 'true';
    document.body.appendChild(script);
  }, [form]);

  const utm = useMemo(() => readUtmParams(), []);
  const productParams = useMemo(() => readProductParams(), []);
  const accent = brand.accent || PROPOSAL_BRAND.accent;
  const fieldFocusStyle = { ['--tw-ring-color' as string]: `${accent}55` };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSubmitting(true);
    setError('');
    try {
      const turnstileInput = document.querySelector<HTMLInputElement>(
        '[name="cf-turnstile-response"]'
      );
      const payload: Record<string, unknown> = {
        ...values,
        ...utm,
        ...productParams,
        landing_page: typeof window !== 'undefined' ? window.location.href : null,
        referrer: typeof document !== 'undefined' ? document.referrer || null : null,
        turnstileToken: turnstileInput?.value || null,
        company_website: values.company_website || '',
      };

      const res = await fetch(`/api/capture/${encodeURIComponent(slug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Submission failed');
      setDone({
        message: json.thankYouMessage || form.thankYouMessage,
        unlockUrl: json.unlockUrl,
        calendarUrl: json.calendarUrl,
      });
      if (embed && window.parent !== window) {
        window.parent.postMessage(
          {
            type: 'foundry-capture-success',
            slug,
            unlockUrl: json.unlockUrl,
            calendarUrl: json.calendarUrl,
          },
          '*'
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Submission failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <FormShell brand={brand} embed={embed}>
        <div className="w-full max-w-[28rem] rounded-card border border-border bg-card overflow-hidden animate-pulse">
          <div className="h-16 bg-muted" />
          <div className="p-6 space-y-4">
            <div className="h-6 w-2/3 bg-muted rounded" />
            <div className="h-10 bg-muted rounded-control" />
            <div className="h-10 bg-muted rounded-control" />
            <div className="h-24 bg-muted rounded-control" />
          </div>
        </div>
      </FormShell>
    );
  }

  if (error && !form) {
    return (
      <FormShell brand={brand} embed={embed}>
        <div className="w-full max-w-[28rem] rounded-card border border-border bg-card p-8 text-center space-y-3">
          <p className="text-sm text-status-red-ink">{error}</p>
        </div>
      </FormShell>
    );
  }

  if (!form) return null;

  const modeHint =
    form.mode === 'gate'
      ? 'Unlock access'
      : form.mode === 'event'
        ? 'Register for this event'
        : 'Tell us a little about you and we will be in touch.';

  const submitLabel =
    form.mode === 'gate' ? 'Unlock' : form.mode === 'event' ? 'Register' : 'Send message';

  if (done) {
    return (
      <FormShell brand={brand} embed={embed}>
        <FormCard brand={brand}>
          <div className="flex items-start gap-3">
            <CheckCircle2 size={22} className="shrink-0 text-status-green-ink mt-0.5" />
            <div className="space-y-2 min-w-0">
              <h1 className="text-lg font-semibold text-primary tracking-tight">You&apos;re all set</h1>
              <p className="text-sm text-muted-foreground leading-relaxed">{done.message}</p>
            </div>
          </div>
          {done.unlockUrl && (
            <a
              href={done.unlockUrl}
              className="inline-flex items-center justify-center w-full py-2.5 text-sm font-semibold rounded-control text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: accent }}
            >
              Get your content
            </a>
          )}
          {done.calendarUrl && (
            <a
              href={done.calendarUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-full py-2.5 text-sm font-semibold rounded-control text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: accent }}
            >
              Add to calendar
            </a>
          )}
          <PoweredByFoundry crmLogoSrc={brand.crmLogoSrc} />
        </FormCard>
      </FormShell>
    );
  }

  return (
    <FormShell brand={brand} embed={embed}>
      <form onSubmit={onSubmit} className="w-full max-w-[28rem] relative">
        <FormCard brand={brand}>
          <div className="space-y-1">
            {brand.companyName && (
              <p
                className="text-[10px] font-semibold tracking-[0.14em] uppercase"
                style={{ color: accent }}
              >
                {brand.companyName}
              </p>
            )}
            <h1 className="text-[1.35rem] font-semibold text-primary tracking-tight leading-snug">
              {form.name}
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">{modeHint}</p>
            {brand.tagline && (
              <p className="text-xs text-muted-foreground/90 pt-0.5">{brand.tagline}</p>
            )}
          </div>

          {form.fields.map((field) => (
            <div key={field.key}>
              <label className="block text-[13px] font-medium text-foreground mb-1.5">
                {field.label}
                {field.required ? <span className="text-status-red-ink"> *</span> : null}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  required={field.required}
                  rows={4}
                  placeholder={fieldPlaceholder(field)}
                  value={values[field.key] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-border rounded-control bg-card placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                  style={fieldFocusStyle}
                />
              ) : field.type === 'select' ? (
                <select
                  required={field.required}
                  value={values[field.key] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-border rounded-control focus:outline-none focus:ring-2 bg-card focus:border-transparent transition-shadow"
                  style={fieldFocusStyle}
                >
                  <option value="">Select…</option>
                  {(field.options || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
                  required={field.required}
                  placeholder={fieldPlaceholder(field)}
                  value={values[field.key] || ''}
                  onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm border border-border rounded-control bg-card placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                  style={fieldFocusStyle}
                />
              )}
            </div>
          ))}

          {form.honeypotEnabled && (
            <div className="absolute -left-[9999px] opacity-0 h-0 w-0 overflow-hidden" aria-hidden>
              <label>
                Company website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={values.company_website || ''}
                  onChange={(e) => setValues((v) => ({ ...v, company_website: e.target.value }))}
                />
              </label>
            </div>
          )}

          {form.turnstileEnabled && form.turnstileSiteKey && (
            <div
              className="cf-turnstile"
              data-sitekey={form.turnstileSiteKey}
              data-theme="light"
            />
          )}

          {error && <p className="text-sm text-status-red-ink">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 text-sm font-semibold rounded-control text-white hover:opacity-90 disabled:opacity-60 transition-opacity shadow-sm"
            style={{ backgroundColor: accent }}
          >
            {submitting ? 'Sending…' : submitLabel}
          </button>

          <PoweredByFoundry crmLogoSrc={brand.crmLogoSrc} />
        </FormCard>
      </form>
    </FormShell>
  );
}
